import {Toast, ToastsList} from "../types/types";
import {clientArrayDecoder, toastResponseDecoder} from "./decoders";
import {byClientCode} from "../utils";
import {createGuid, GUID} from "../guids";
import {ClientCode, ClientConfig} from "../types/clients";

interface Result {
  toasts: Toast[]
  success: boolean
}

async function getClientsBase(includeInactive: boolean): Promise<ReadonlyArray<ClientConfig>> {
  const queryParam = includeInactive ? '?includeInactive=true' : ''
  const response = await fetch(`/api/clients/${queryParam}`, {
    method: 'GET',
  })

  if (!response.ok) {
    throw response.statusText
  }

  const json = await response.json()
  const clients = clientArrayDecoder.runWithException(json)

  return [...clients].sort(byClientCode)
}

/**
 * Retrieves a list of **active** client configurations.
 *
 * @return {Promise<ReadonlyArray<ClientConfig>>} A promise that resolves to a read-only array of client configurations.
 */
export async function getActiveClients(): Promise<ReadonlyArray<ClientConfig>> {
  return getClientsBase(false)
}

/**
 * Retrieves a list of all clients, including inactive.
 *
 * @return {Promise<ReadonlyArray<ClientConfig>>} A promise that resolves to a readonly array of client configurations.
 */
export async function getAllClients(): Promise<ReadonlyArray<ClientConfig>> {
  return getClientsBase(true)
}

export async function postAddClient(client: ClientConfig): Promise<Result> {
  const response = await fetch(`/api/clients/`, {
    body: convertClientToBackendCompatibleJsonString(client),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
  })

  if (!response.ok) {
    return {toasts: [await getFailedToast(response, client.code)], success: false}
  }

  const toasts: ToastsList = await getSuccessResponse(response)

  const successToast: Toast = {
    content: `New client ${client.code} saved successfully`,
    header: 'Save Client Completed',
    id: createGuid(),
  }

  return {toasts: [successToast, ...toasts], success: true};
}

function convertClientToBackendCompatibleJsonString(client: ClientConfig, forceUpdateExchangeLimit?: boolean): string {
  return JSON.stringify({
    ...client,
    alerts: client.alerts.map(alert => ({
      ...alert,
      contacts: [...alert.contacts],
    })),
    virtualServers: [...client.virtualServers],
    forceUpdateExchangeLimit: forceUpdateExchangeLimit ?? false
  }
  )}

export async function putClientUpdate(client: ClientConfig, originalClientId: GUID, forceUpdateExchangeLimit?: boolean): Promise<Result> {

  const response = await fetch(
    `/api/clients/${originalClientId}`,
    {
      body:
        convertClientToBackendCompatibleJsonString(client, forceUpdateExchangeLimit),
      headers: {
        'Content-Type': 'application/json',
        
      },
      method: 'PUT',
    },
  )

  if (!response.ok) {
    return {toasts: [await getFailedToast(response, client.code)], success: false}
  }

  const toasts: ToastsList = await getSuccessResponse(response)

  const successToast: Toast = {
    content: `Changes to client ${
      client.code
    } saved successfully`,
    header: 'Save Client Completed',
    id: createGuid(),
  }

  return {toasts: [successToast, ...toasts], success: true}
}

export async function updateTCSLimits(): Promise<ToastsList> {
  const response = await fetch('/api/clients/updatetcslimits', {
    method: 'POST',
  })

  if (!response.ok) {
    throw response.statusText
  }

  const toasts: ToastsList = await getSuccessResponse(response)

  const completedOperationToast: Toast = {
    content: 'TCS limit update operation completed.',
    header: 'TCS Limit Update Completed',
    id: createGuid(),
  }

  return [completedOperationToast, ...toasts]
}

async function getSuccessResponse(response: Response): Promise<ToastsList> {
  const body = await response.json()
  const errors = toastResponseDecoder.runWithException(body)
  const toasts: ToastsList = errors.map(e => {
    const toast: Toast = {
      header: e.title,
      content: e.message,
      id: createGuid(),
    }
    return toast
  })
  return toasts
}

async function getFailedToast(response: Response, clientCode: ClientCode): Promise<Toast> {
  const body = await response.json()
  const errors = toastResponseDecoder.runWithException(body)
  // There should only be 1 error in the repsponse, if more then join them
  const errorMessages = ' - ' + errors ? errors.map(e => { return e.message }).join(', ') : ''
  return {
    content: `Save request for ${
      clientCode
      } failed (${response.status} - ${response.statusText}${errorMessages})`,
    header: 'Failed to save client',
    id: createGuid(),
  }
}

export async function getAppVersion(): Promise<string> {
  const response = await fetch(`/api/config/AppVersion`, {
    method: 'GET',
  })

  if (response.status === 401) {
    throw new Error('Unauthorised access')
  }

  if (!response.ok) {
    throw response.statusText
  }
  const json = await response.json();
  return json.appVersion || 'unknown';
}
